import { isNil, isString, sortBy } from 'lodash';

import { getElementInfo } from 'utils/element';

import type {
  MixedSelectDropdownElementInfo,
  MixedSelectDropdownItem,
  MixedSelectDropdownItemProps
} from 'types';

export const COLORS_MAP = {
  '#e2629c': 'pink',
  '#be8fd7': 'lavender',
  '#026489': 'sky',
  '#4dc2ee': 'pool',
  '#aecc67': 'grass',
  '#f4c63b': 'gold',
  'current': 'currentcolor'
};

// we need to limit some chars (\, /, %, etc.) b/c of the regex in the function (`filterItemByValue`) below
const allowedChars = /[^a-z\u00C0-\u00FF&0-9_.-]/ig;

const sanitizeString = (value: string): string => (
  isString(value) ? value.replace(/\s/g, '-').toLowerCase() : ''
);

const filterItemByValue = (
  item: MixedSelectDropdownItem,
  value: string
): boolean => {
  const safeValue = sanitizeString(value);
  const updatedValue = safeValue.replace(allowedChars, '');
  const regex = new RegExp(updatedValue, 'i');

  return regex.test(sanitizeString(item.label || ''));
};

export const filterItems = (
  items: Array<MixedSelectDropdownItem>,
  value: string
): Array<MixedSelectDropdownItem> => (
  value ? items.filter(item => filterItemByValue(item, value)) : items
);

export const getElementDetails = (
  ref?: HTMLElement | null
): MixedSelectDropdownElementInfo | undefined | null => {
  const elementInfo = getElementInfo(ref);

  return elementInfo && {
    bottom: elementInfo.bottom,
    left: elementInfo.left,
    width: elementInfo.width,
    height: elementInfo.height
  };
};

export const sortItems = (
  items: Array<MixedSelectDropdownItem>,
  withSortable?: boolean | null
): Array<MixedSelectDropdownItem> => (
  withSortable ? sortBy(items, 'label') : items
);

export const getSelectedItems = (
  items: Array<MixedSelectDropdownItem>
): Array<MixedSelectDropdownItem> => (
  (items || []).filter(item => item.isSelected)
);

export const getUnselectedItems = (
  items: Array<MixedSelectDropdownItem>
): Array<MixedSelectDropdownItem> => (
  (items || []).filter(item => !item.isSelected)
);

export const selectAllItems = (
  items: Array<MixedSelectDropdownItem>
): Array<MixedSelectDropdownItem> => (
  (items || []).map(item => ({
    ...item,
    isSelected: true
  }))
);

export const deselectAllItems = (
  items: Array<MixedSelectDropdownItem>
): Array<MixedSelectDropdownItem> => (
  (items || []).map(item => ({
    ...item,
    isSelected: false
  }))
);

export const removeItemFromList = (
  items: Array<MixedSelectDropdownItem>,
  identifier: string
): Array<MixedSelectDropdownItem> => (
  (items || []).filter(item => item.identifier !== identifier)
);

export const getRemovedItem = (
  items: Array<MixedSelectDropdownItem>,
  identifier: string
): Partial<MixedSelectDropdownItem> => {
  const removedItem = items.find(item => item.identifier === identifier);

  if (isNil(removedItem)) {
    return {};
  }

  return {
    ...removedItem,
    isSelected: false
  };
};

export const getUpdatedItem = (
  items: Array<MixedSelectDropdownItem>,
  identifier: string
): Partial<MixedSelectDropdownItem> => {
  const updatedItem = items.find(item => item.identifier === identifier);

  if (isNil(updatedItem)) {
    return {};
  }

  return {
    ...updatedItem,
    isSelected: true
  };
};

export const updatePropValueByIdentifier = (
  items: Array<MixedSelectDropdownItem>,
  identifier: string | undefined,
  prop: MixedSelectDropdownItemProps,
  value: any
): Array<MixedSelectDropdownItem> => (items || []).map(item => ({
  ...item,
  [String(prop)]: item.identifier === identifier ? value : item[prop]
}));
